import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef} from 'react'
import styles from './PullRequestMiniHeader.module.css'
import {useLayoutQuery} from '../page-data/loaders/use-layout-query'
import type {HeaderPageData} from '../page-data/payloads/header'
import {getRouteConfig} from '../routes/route-manager'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {useUserCopilotAccess} from '../hooks/use-user-copilot-access'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {OpenCommentsPanelButton} from './toolbar/OpenCommentsPanelButton'
import type {PullRequestHeaderProps} from './PullRequestHeader'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureState, useIsOverviewEnabled} from '../hooks/use-feature-state'
import {FeatureState} from '../page-data/payloads/header'
import {isLoggedIn, getCurrentUserLogin} from '@github-ui/client-env'
import {ReviewMenuButton} from './toolbar/ReviewMenuButton'
import {OpenCopilotChatPanelButton} from './toolbar/OpenCopilotChatPanelButton'
import {OpenDockedPanelButton} from './toolbar/OpenDockedPanelButton'
import {useUnifiedBatchCount} from '../stores/layout-selectors'
import {MiniHeaderTabSwitcher} from './MiniHeaderTabSwitcher'
import {HeaderDivider} from './HeaderDivider'
import {ManageBatchHeaderButton} from './toolbar/ManageBatchHeaderButton'
import {PullRequestCopyLinkButton} from './PullRequestCopyLinkButton'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import {useMediaQuery} from '@github-ui/use-media-query'
import {FeatureFlags} from '@primer/react/experimental'
import {StackState} from './StackState'

export type PullRequestMiniHeaderProps = PullRequestHeaderProps & {showCompactNavigation?: boolean}

export function PullRequestMiniHeader({
  activeRoute,
  showCompactNavigation,
  ...initialData
}: PullRequestMiniHeaderProps) {
  const {helpUrl} = useLayoutQuery().data
  const {data: headerData} = useLayoutQuery({...initialData, helpUrl})
  return (
    <PullRequestMiniHeaderContent
      {...headerData}
      activeRoute={activeRoute}
      showCompactNavigation={showCompactNavigation}
    />
  )
}

export type PullRequestMiniHeaderContentProps = Omit<HeaderPageData, 'helpUrl'> & {
  activeRoute: PullRequestHeaderProps['activeRoute']
  showCompactNavigation?: boolean
}

export function PullRequestMiniHeaderContent({
  activeRoute,
  pullRequest,
  repository,
  mergeStatusButtonData,
  user,
  stack,
  urls,
  showCompactNavigation = false,
}: PullRequestMiniHeaderContentProps) {
  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const overviewEnabled = useIsOverviewEnabled()
  const {dockedPanelSupported} = getRouteConfig(activeRoute, overviewEnabled)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const hasValidatedMergeData = validatedMergeData !== null
  const batchCount = useUnifiedBatchCount()
  const {helpUrl} = useLayoutQuery().data
  const titleRef = useRef<HTMLAnchorElement>(null)
  const isNarrow = useMediaQuery('(max-width: 767.98px)')
  const hasCopilotAccess = useUserCopilotAccess()

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  const showManageBatchHeaderButton = isDockedMode && isFeatureEnabled('unified_batch_pr_comments') && batchCount > 0

  return (
    <div className={styles.miniHeaderInner}>
      <MiniHeaderTabSwitcher
        activeRoute={activeRoute}
        pullRequest={pullRequest}
        repository={repository}
        urls={urls}
        hidden={!showCompactNavigation}
        fallbackFocusRef={titleRef}
      />
      {showCompactNavigation && <HeaderDivider className={styles.navigationDivider} />}
      <div className={styles.metadata}>
        <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
          <PullRequestStateLabel
            className={styles.stateLabel}
            state={pullRequest.state}
            size="small"
            hidden={!showCompactNavigation || isNarrow}
            fallbackFocusRef={titleRef}
          />
        </FeatureFlags>
        {showCompactNavigation && !isNarrow && (
          <StackState
            pullRequestNumber={pullRequest.number}
            stack={stack}
            viewerCanUnstack={user.canEditTitle}
            size="small"
          />
        )}
        <span className={clsx(styles.number, 'f5 fgColor-muted')}>#{pullRequest.number}</span>
        <a ref={titleRef} href="#top" className={clsx(styles.title, 'f5 text-bold fgColor-default')}>
          <VerifiedHTMLText className="markdown-title" html={pullRequest.titleHtml} />
        </a>
        <PullRequestCopyLinkButton
          owner={repository.ownerLogin}
          repository={repository.name}
          number={pullRequest.number}
          titleHtml={pullRequest.titleHtml}
        />
      </div>
      <div className={styles.actions}>
        {isLoggedIn() && validatedMergeData && (
          <MergeStatusButtonWrapper
            channels={validatedMergeData.channels}
            headSha={validatedMergeData.headSha}
            helpUrl={helpUrl}
            viewerLogin={validatedMergeData.viewerLogin}
            isDockedMode={isDockedMode}
            onToggleDockedPanel={handleToggleDockedMergePanel}
            mergeStatusButtonRef={mergeStatusButtonRef}
            size="small"
          />
        )}
        {currentUserLogin &&
          (showManageBatchHeaderButton ? (
            <ManageBatchHeaderButton
              className="tmp-ml-2"
              count={batchCount}
              entrySurface="sticky_header_manage_batch_button"
              size="small"
            />
          ) : (
            <ReviewMenuButton
              className="tmp-ml-2"
              currentUserLogin={currentUserLogin}
              pullRequest={pullRequest}
              headRefOid={pullRequest.headSha}
              repository={repository}
              overlayPortal="external"
              redirectOnMutation={!(activeRoute === 'conversation' || activeRoute === 'overview')}
              stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
              size="small"
            />
          ))}
        <HeaderDivider className="hide-sm tmp-mx-3" />
        <OpenCommentsPanelButton className="hide-sm hide-md" showMarkerCounts={false} size="small" />
        {hasCopilotAccess && <OpenCopilotChatPanelButton size="small" className="hide-sm hide-md tmp-ml-2" />}
        <OpenDockedPanelButton size="small" className="tmp-ml-2" />
      </div>
    </div>
  )
}
